import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Autocomplete,
  Chip,
  Button,
  TextField,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteIcon from "@mui/icons-material/Delete";
import { Event } from "../Events";
import axios from "axios";
import { useUser } from "../../UserContext";

interface PhotosProps {
  eventData: Event;
  username?: string;
}

interface Profile {
  username_id: number;
  username: string;
  type: string;
  additional_details: {
    first_name?: string;
    last_name?: string;
    name?: string;
    account?: string;
  };
}

interface PhotoTag {
  id: number;
  photo_id: number;
  profile_id: number;
  profile?: Profile;
}

interface Photo {
  id: number;
  photo_url: string;
  uploaded_by: number;
  tags?: PhotoTag[];
}

// Add a helper function to get the display name
const getDisplayName = (profile: Profile): string => {
  if (
    profile.additional_details.first_name &&
    profile.additional_details.last_name
  ) {
    return `${profile.additional_details.first_name} ${profile.additional_details.last_name}`;
  }
  return profile.additional_details.name || profile.username;
};

const Photos: React.FC<PhotosProps> = ({ eventData, username }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const { currentUser } = useUser();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedProfiles, setSelectedProfiles] = useState<Profile[]>([]);
  const [taggingPhoto, setTaggingPhoto] = useState<Photo | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  useEffect(() => {
    fetchPhotos();
    fetchProfiles();
  }, [eventData.event_id]);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/photos/event/${eventData.event_id}`,
        {
          withCredentials: true,
          headers: {
            Accept: "application/json",
          },
        }
      );
      setPhotos(response.data);
    } catch (error) {
      console.error("Error fetching photos:", error);
    }
  };

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/profiles/`,
        {
          params: { username: username },
        }
      );
      setProfiles(response.data);
    } catch (error) {
      console.error("Error fetching profiles:", error);
    }
  };

  const handlePhotoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("photo", file);
    formData.append("userId", currentUser?.user_id.toString() || "");

    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/photos/event/${eventData.event_id}`,
        formData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        fetchPhotos(); // Refresh photos after successful upload
      }
    } catch (error) {
      console.error("Error uploading photo:", error);
      // You might want to add some user feedback here
    }
  };

  const handleAddTags = async (photo: Photo) => {
    setTaggingPhoto(photo);
    setSelectedProfiles(
      photo.tags?.map((tag) => tag.profile!).filter(Boolean) || []
    );
  };

  const handleSaveTags = async () => {
    if (!taggingPhoto) return;

    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/photos/${taggingPhoto.id}/tags`,
        {
          profileIds: selectedProfiles.map((p) => p.username_id),
        },
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      fetchPhotos();

      // Use the new getDisplayName function for the success message
      const taggedNames = selectedProfiles
        .map((profile) => getDisplayName(profile))
        .join(", ");

      setSnackbar({
        open: true,
        message:
          selectedProfiles.length > 0
            ? `Successfully tagged ${taggedNames}`
            : "All tags have been removed",
        severity: "success",
      });

      setTaggingPhoto(null);
    } catch (error) {
      console.error("Error saving tags:", error);
      setSnackbar({
        open: true,
        message: "Failed to save tags",
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleDeletePhoto = async (photo: Photo) => {
    setPhotoToDelete(photo);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!photoToDelete) return;

    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/photos/${photoToDelete.id}`,
        {
          withCredentials: true,
          headers: {
            Accept: "application/json",
          },
        }
      );
      fetchPhotos(); // Refresh photos after deletion
      setDeleteConfirmOpen(false);
      setPhotoToDelete(null);
    } catch (error) {
      console.error("Error deleting photo:", error);
    }
  };

  const renderPhotoGridItem = (photo: Photo) => (
    <Grid item xs={4} sm={4} md={4} key={photo.id}>
      <Box
        sx={{
          position: "relative",
          paddingTop: "100%",
          cursor: "pointer",
          "&:hover .photo-controls": {
            opacity: 1,
          },
        }}
        onClick={() => setSelectedPhoto(photo.photo_url)}
      >
        <Box
          component="img"
          src={photo.photo_url}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
        {/* Top tag list */}
        {photo.tags && photo.tags.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              padding: 1,
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
              color: "white",
              fontSize: "0.8rem",
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {photo.tags.map((tag) => (
              <Chip
                key={tag.id}
                label={tag.profile ? getDisplayName(tag.profile) : ""}
                size="small"
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  color: "white",
                  "& .MuiChip-label": {
                    fontSize: "0.7rem",
                  },
                }}
              />
            ))}
          </Box>
        )}

        {/* Controls overlay - appears on hover */}
        <Box
          className="photo-controls"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: 1,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 70%, rgba(0,0,0,0.4) 100%)",
            opacity: 0,
            transition: "opacity 0.2s ease-in-out",
          }}
        >
          {/* Top controls */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleAddTags(photo);
              }}
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                },
                padding: "4px",
              }}
              size="small"
            >
              <PersonAddIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Bottom controls */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleDeletePhoto(photo);
              }}
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                },
                color: "error.main",
                padding: "4px",
              }}
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Grid>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Grid
        container
        spacing={0} // Remove spacing between grid items
        sx={{
          width: "100%",
          margin: 0, // Remove margin
        }}
      >
        {/* Upload Button */}
        <Grid item xs={4} sm={4} md={4}>
          <Box
            sx={{
              position: "relative",
              paddingTop: "100%", // Keep 1:1 Aspect Ratio
              backgroundColor: "#f5f5f5",
            }}
          >
            <IconButton
              component="label"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handlePhotoUpload}
              />
              <AddIcon sx={{ fontSize: 40 }} />
            </IconButton>
          </Box>
        </Grid>

        {/* Photo Grid */}
        {photos.map(renderPhotoGridItem)}
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this photo? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Modal */}
      <Dialog
        open={Boolean(selectedPhoto)}
        onClose={() => setSelectedPhoto(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "hidden",
          },
        }}
      >
        <DialogContent
          sx={{
            padding: 0,
            backgroundColor: "transparent",
            "&:first-of-type": {
              paddingTop: 0,
            },
          }}
        >
          {selectedPhoto && (
            <Box
              component="img"
              src={selectedPhoto}
              sx={{
                display: "block",
                maxWidth: "90vw",
                maxHeight: "90vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPhoto(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Tagging Dialog */}
      <Dialog
        open={Boolean(taggingPhoto)}
        onClose={() => setTaggingPhoto(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <Autocomplete
            multiple
            options={profiles}
            value={selectedProfiles}
            onChange={(_, newValue) => setSelectedProfiles(newValue)}
            getOptionLabel={(option) => getDisplayName(option)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Tag People"
                placeholder="Search people..."
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={getDisplayName(option)}
                  {...getTagProps({ index })}
                />
              ))
            }
          />
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setTaggingPhoto(null)} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button onClick={handleSaveTags} variant="contained">
              Save Tags
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Add Snackbar at the end */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Photos;
